/* eslint-disable react/prop-types */
import React, { useState, useEffect } from "react";
import SearchSelector from "./SearchSelector";
import moment from "moment";
import Http from "../services/http";

const StatisticQueryInput = ({
  onQueryResult,
  setIsLoading,
  initialParams,
}) => {
  const [startYear, setStartYear] = useState(initialParams.startYear || "");
  const [startMonth, setStartMonth] = useState(initialParams.startMonth || "");
  const [endYear, setEndYear] = useState(initialParams.endYear || "");
  const [endMonth, setEndMonth] = useState(initialParams.endMonth || "");
  const [selectedProcess, setSelectedProcess] = useState(
    initialParams.constructionWorkID
  );
  const [selectedProcessName, setSelectedProcessName] = useState(
    initialParams.constructionWorkName
  );
  const [selectedVendor, setSelectedVendor] = useState(
    initialParams.supplierID
  );
  const [selectedVendorName, setSelectedVendorName] = useState(
    initialParams.supplierName
  );
  const [processOptions, setProcessOptions] = useState([]);
  const [vendorOptions, setVendorOptions] = useState([]);
  const [isUnlimitedTime, setIsUnlimitedTime] = useState(
    initialParams.isUnlimitedTime === "true"
  );

  const currentYear = new Date().getFullYear();
  // Generate year options (current year - 10 years)
  const years = Array.from({ length: 11 }, (_, i) =>
    (currentYear - i).toString()
  );
  const months = Array.from({ length: 12 }, (_, i) =>
    (i + 1).toString().padStart(2, "0")
  );

  // Initialize time values from URL parameters
  useEffect(() => {
    if (initialParams.isUnlimitedTime === "true") {
      setIsUnlimitedTime(true);
    } else {
      setIsUnlimitedTime(false);
    }
  }, [initialParams.isUnlimitedTime]);

  useEffect(() => {
    const fetchOptions = async () => {
      try {
        // Fetch process options
        const processResponse = await Http.get("auth/constructionWork/list");
        setProcessOptions([
          { id: "", name: "所有工程" },
          ...(processResponse.constructionWorkList || []),
        ]);

        // Fetch vendor options
        const vendorResponse = await Http.get("auth/supplier/list");
        setVendorOptions([
          { id: "", name: "所有廠商" },
          ...(vendorResponse.supplierList || []),
        ]);
      } catch (error) {
        console.error("Error fetching options:", error);
      }
    };

    fetchOptions();
  }, []);

  const handleSearch = async () => {
    setIsLoading(true);
    try {
      const queryParams = {
        startYear: startYear,
        startMonth: startMonth,
        endYear: endYear,
        endMonth: endMonth,
        isUnlimitedTime: isUnlimitedTime.toString(),
        constructionWorkID: selectedProcess || undefined,
        supplierID: selectedVendor || undefined,
        constructionWorkName: selectedProcessName,
        supplierName: selectedVendorName,
      };

      const response = await Http.get("auth/statistic/comprehensiveV2", {
        params: {
          startYear: isUnlimitedTime ? undefined : startYear,
          startMonth: isUnlimitedTime ? undefined : startMonth,
          endYear: isUnlimitedTime ? undefined : endYear,
          endMonth: isUnlimitedTime ? undefined : endMonth,
          constructionWorkID: selectedProcess || undefined,
          supplierID: selectedVendor || undefined,
        },
      });

      onQueryResult(response, queryParams);
    } catch (error) {
      console.error("Error fetching statistics:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="mx-10 mt-5 border-2 border-gray-300 rounded-md p-5">
      <div className="flex flex-col items-center gap-4">
        <div className="flex items-center gap-16">
          <div className="flex items-center gap-4">
            <span className="text-xl">開始時間</span>
            <select
              className="select select-bordered text-xl"
              value={startYear}
              onChange={(e) => setStartYear(e.target.value)}
              disabled={isUnlimitedTime}
            >
              {years.map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
            <span className="text-xl">年</span>
            <select
              className="select select-bordered text-xl"
              value={startMonth}
              onChange={(e) => setStartMonth(e.target.value)}
              disabled={isUnlimitedTime}
            >
              {months.map((month) => (
                <option key={month} value={month}>
                  {month}
                </option>
              ))}
            </select>
            <span className="text-xl">月</span>
          </div>

          <div className="flex items-center gap-4">
            <span className="text-xl">結束時間</span>
            <select
              className="select select-bordered text-xl"
              value={endYear}
              onChange={(e) => setEndYear(e.target.value)}
              disabled={isUnlimitedTime}
            >
              {years.map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
            <span className="text-xl">年</span>
            <select
              className="select select-bordered text-xl"
              value={endMonth}
              onChange={(e) => setEndMonth(e.target.value)}
              disabled={isUnlimitedTime}
            >
              {months.map((month) => (
                <option key={month} value={month}>
                  {month}
                </option>
              ))}
            </select>
            <span className="text-xl">月</span>
          </div>
        </div>

        <div className="flex flex-col gap-4">
          <div className="flex items-center gap-16">
            <div className="flex items-center gap-4">
              <span className="text-xl">工程</span>
              <div className="w-[300px]">
                <SearchSelector
                  title="所有工程"
                  value={selectedProcess}
                  itemList={processOptions}
                  onChange={(item) => {
                    setSelectedProcess(item.id);
                    setSelectedProcessName(item.name);
                    if (!item.id) {
                      setIsUnlimitedTime(false);
                    }
                  }}
                />
              </div>
            </div>

            <div className="flex items-center gap-4">
              <span className="text-xl">廠商</span>
              <div className="w-[300px]">
                <SearchSelector
                  title="所有廠商"
                  value={selectedVendor}
                  itemList={vendorOptions}
                  onChange={(item) => {
                    setSelectedVendor(item.id);
                    setSelectedVendorName(item.name);
                  }}
                />
              </div>
            </div>
          </div>

          {selectedProcess && (
            <div className="flex items-center gap-16">
              <div className="flex items-center gap-4">
                <span className="text-xl opacity-0">工程</span>
                <div className="w-[300px]">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      className="checkbox"
                      checked={isUnlimitedTime}
                      onChange={(e) => setIsUnlimitedTime(e.target.checked)}
                    />
                    <span className="text-lg">不限時間</span>
                  </label>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-center">
          <button
            className="btn btn-primary text-xl px-8"
            onClick={handleSearch}
          >
            查詢
          </button>
        </div>
      </div>
    </div>
  );
};

export default StatisticQueryInput;
